import { BOTONES } from '../../../../../componentes/botones';
import { Component, computed, inject, Signal, signal } from '@angular/core';

import {
  AlarmasDomiciliariasService,
  DatosConfigNanocomm,
} from '../alarmas-domiciliarias.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { IDispositivoAlarma, ISim } from 'modelos/src';
import { asapScheduler } from 'rxjs';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { InfoBannerComponent } from '../../../../../componentes/info-banner/info-banner.component';
import { ContentCardComponent } from '../../../../../componentes/content-card/content-card.component';
import { ModeloDispositivoSelectorCacheService } from '../../../../../componentes/selectores/modelo-dispositivo-selector/modelo-dispositivo-selector-cache.service';

@Component({
  selector: 'app-configurar-comunicador',
  templateUrl: './configurar-comunicador.component.html',
  styleUrl: './configurar-comunicador.component.scss',
  standalone: true,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectModule,
    BOTONES,
    MatIconModule,
    CardHeaderComponent,
    InfoBannerComponent,
    ContentCardComponent,
  ],
})
export class ConfigurarComunicadorComponent {
  private service = inject(AlarmasDomiciliariasService);
  private dialog = inject(DialogService);
  private helper = inject(HelperService);
  private modeloCache = inject(ModeloDispositivoSelectorCacheService);

  public loading = signal(false);
  public valid = signal(false);
  // Búsqueda del ng-select de SIM por número o APN.
  public buscarSim = (term: string, sim: ISim) =>
    `${sim.numero ?? ''} - ${sim.apn ?? ''}`
      .toLowerCase()
      .includes(term.toLowerCase());
  private regex = /^(?!0|15)\d{10}$/;

  public data = inject(MAT_DIALOG_DATA) as IDispositivoAlarma;
  public dialogRef = inject(MatDialogRef);
  public datos?: DatosConfigNanocomm;

  public selected = signal<ISim | undefined>(undefined);

  /**
   * El comunicador es opcional: si la alarma no tiene uno asignado el formato
   * lo aporta el modelo del panel (comunicación integrada). Este diálogo
   * configura siempre el PRINCIPAL (comunicadores[0]) — resuelto por
   * idComunicador contra el cache de modelos, no viene pre-poblado.
   */
  private get formato() {
    return (
      this.modeloCache.getModeloPorIdSync(
        this.data?.comunicadores?.[0]?.idComunicador,
      )?.formatoMensaje ?? this.data?.modelo?.formatoMensaje
    );
  }

  public sims: ISim[] = [];
  public simsValidas: Signal<ISim[]> = computed(() => {
    return this.sims.filter((sim) => this.esSimValida(sim));
  });

  public tieneSIMsValidas: Signal<boolean> = computed(() => {
    return this.simsValidas().length > 0;
  });

  public mensajePreview: Signal<string | undefined> = computed(() => {
    this.datos.datos.apn = this.selected()?.apn;
    this.datos.datos.pwd = this.selected()?.password;
    this.datos.datos.usr = this.selected()?.usuario;
    this.datos.telefono = this.selected()?.numero;
    if (this.formato === 'Nanocomm ED5200') {
      this.datos.datos.modelo = 'ED5200';
    }

    asapScheduler.schedule(() => this.valid.set(this.validar()));

    return this.armarString(this.selected());
  });

  public error = signal<string | undefined>(undefined);

  constructor() {
    this.sims.push(this.data.sim1);
    this.sims.push(this.data.sim2);
    // Warm-up: `formato` (getter síncrono) necesita el cache de modelos tibio.
    this.modeloCache.getModelos().subscribe();

    this.datos = {
      datos: {
        idComunicador: this.data.idUnicoComunicador,
        passComunicador: this.data.comunicadores?.[0]?.passwordComunicador,
        apn: null,
        pwd: null,
        usr: null,
        modelo: null,
      },
      telefono: null,
    };
  }

  public esSimValida(sim: ISim): boolean {
    return !!(sim?.numero && sim?.apn && sim?.usuario && sim?.password);
  }

  private armarString(sim: ISim): string | undefined {
    if (!sim) return undefined;
    if (this.formato === 'Nanocomm ED5200') {
      return `ED5200*${this.data.comunicadores?.[0]?.passwordComunicador},ID=${this.data.idUnicoComunicador},${sim.apn}=${sim.usuario},PWD=${sim.password},IP=123.456.789.000,PORT=1337,,,`;
    } else {
      return 'Formato no soportado';
    }
  }

  private validar() {
    if (!this.selected()) {
      this.error.set('Seleccioná una SIM');
      return false;
    }
    if (
      !this.datos.datos.apn ||
      !this.datos.datos.pwd ||
      !this.datos.datos.usr
    ) {
      this.error.set('Completá los campos de APN, usuario y contraseña');
      return false;
    }
    if (!this.datos.telefono) {
      this.error.set('Completá el campo de teléfono');
      return false;
    }
    if (!this.regex.test(this.datos.telefono)) {
      this.error.set('El teléfono es inválido');
      return false;
    }
    if (!this.datos.datos.idComunicador) {
      this.error.set('Completá el campo de ID del comunicador');
      return false;
    }
    if (!this.datos.datos.passComunicador) {
      this.error.set('Se requiere la contraseña del comunicador');
      return false;
    }
    this.error.set(undefined);
    return true;
  }

  public async enviarConfig() {
    const res = await this.dialog.confirm(
      'Confirmar envío',
      '¿Confirmás el envío de la configuración?',
    );
    if (!res) return;
    this.loading.set(true);
    try {
      await this.service.enviarSMSNanocomm(this.datos);
      this.helper.notifSuccess('Configuración enviada');
      this.dialogRef.close();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading.set(false);
    }
  }
}
